/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { DOCUMENT_CONFIGS } from './documentVersions.config';
import type { DocumentType, DocumentVersion, DocumentConfig } from './documentVersions.config';

export { DOCUMENT_CONFIGS } from './documentVersions.config';
export type { DocumentType, DocumentVersion, DocumentConfig } from './documentVersions.config';

/**
 * Gets the latest version number for a document type.
 *
 * @param documentType - The type of document
 * @returns The latest version number
 */
export function getLatestVersion(documentType: DocumentType): number {
  const config = DOCUMENT_CONFIGS[documentType];
  if (!config || config.versions.length === 0) {
    throw new Error(`No versions found for document type: ${documentType}`);
  }
  return Math.max(...config.versions.map((v) => v.version));
}

/**
 * Gets the document configuration for a given type.
 *
 * @param documentType - The type of document
 * @returns The document configuration
 */
export function getDocumentConfig(documentType: DocumentType): DocumentConfig {
  const config = DOCUMENT_CONFIGS[documentType];
  if (!config) {
    throw new Error(`Unknown document type: ${documentType}`);
  }
  return config;
}

/**
 * Gets the path to a specific version of a document.
 *
 * @param documentType - The type of document
 * @param version - The version number, or 'latest' for the latest version
 * @returns The path to the document version
 */
export function getDocumentVersionPath(
  documentType: DocumentType,
  version: number | 'latest'
): string {
  const config = getDocumentConfig(documentType);
  const versionNumber = version === 'latest' ? getLatestVersion(documentType) : version;
  return `${config.basePath}/v${versionNumber}`;
}

/**
 * Resolves a version string to a version number.
 * Handles 'latest' alias by resolving to the latest version number.
 *
 * @param documentType - The type of document
 * @param versionString - The version string (e.g., '1', 'latest', 'v1')
 * @returns The version number
 */
export function resolveVersion(
  documentType: DocumentType,
  versionString: string
): number {
  if (versionString === 'latest') {
    return getLatestVersion(documentType);
  }
  
  // Remove 'v' prefix if present
  const cleaned = versionString.replace(/^v/i, '');
  const version = parseInt(cleaned, 10);
  
  if (isNaN(version)) {
    throw new Error(`Invalid version string: ${versionString}`);
  }
  
  return version;
}

/**
 * Gets the filename for a document version in a specific language.
 *
 * @param documentType - The type of document
 * @param language - The language code (e.g., 'en', 'pt-BR', 'es')
 * @param version - Optional version number for versioned path resolution
 * @returns The filename path
 */
export function getDocumentFilename(
  documentType: DocumentType,
  language: string,
  version?: number
): string {
  const config = getDocumentConfig(documentType);
  return config.filenamePattern(language, version);
}

/**
 * Normalizes a language code to a supported format.
 *
 * @param language - The language code to normalize
 * @returns The normalized language code
 */
export function normalizeLanguage(language: string): string {
  const languageMap: Record<string, string> = {
    'en': 'en',
    'pt-BR': 'pt-BR',
    'pt': 'pt-BR',
    'es': 'es',
  };
  
  const normalized = languageMap[language] || 
    languageMap[language.split('-')[0]] || 
    'en';
  
  return normalized;
}
